/**
 * The measure chip (cb-19 · 1): grid · `1,420 sq ft` · pencil, or `Bed area —`
 * when there is no number yet.
 *
 * One control for the one number a measured thing scales from, on a quote's
 * service row and on a package group's header alike (Aaron, review item 4.1:
 * the package's box "should be exactly the same as the linked-measurement sf
 * box for services"). It is a button, not a box: it opens the Measure popover,
 * where the number is looked up from the property rather than typed, and
 * which says what kind of measurement it is and whether it follows the
 * property or is this quote's own.
 *
 * 36px, the height of the quantity boxes on the lines under it (Aaron, step
 * 21 item 3.1) — it was 30 and read as a tag.
 */
import { type MouseEvent } from 'react'
import { cn } from '../../lib/cn.js'
import { formatNumber } from '../../lib/format.js'
import { IconEdit, IconGrid } from '../../frame/icons.js'
import { MEASURE_CHIP_EMPTY } from '../copy.js'

export interface MeasureChipProps {
  measure: number | null
  unit: string | null
  /** The kind of measurement — `Bed area` — which is all the empty chip says. */
  measurementName: string | null
  /** The Measure popover is open off this chip — the accent ring (cb-19 · 1). */
  open?: boolean
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
  /** A quote that is not a draft: the measure is read, not changed (folder 52). */
  disabled?: boolean
}

export function MeasureChip({ measure, unit, measurementName, open = false, onClick, disabled = false }: MeasureChipProps) {
  return (
    <button
      type="button"
      data-measure-chip=""
      onClick={onClick}
      disabled={disabled}
      aria-expanded={open}
      className={cn(
        'inline-flex h-9 min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap',
        'rounded-control border px-2.5 text-[13px] font-semibold text-text',
        open
          ? 'border-accent-line bg-accent-soft ring-2 ring-accent-ring'
          : 'border-line bg-surface hover:border-line-strong',
      )}
    >
      <span className="shrink-0 text-text-muted"><IconGrid size={13} /></span>
      {measure === null
        ? <span className="truncate">{MEASURE_CHIP_EMPTY(measurementName ?? '')}</span>
        : (
          <span className="tnum truncate">
            {chipMeasure(measure)} {unit}
          </span>
        )}
      <span className="shrink-0 text-text-faint"><IconEdit size={12} /></span>
    </button>
  )
}

/**
 * The measure as the chip says it: whole units from 100 up, one decimal below.
 *
 * Display only — the stored number keeps every decimal it was given. A walked
 * or aerial measurement arrives as `30240.223`, and printed whole the chip
 * ran out of room and cut off the unit, which is the half of `30,240 sq ft`
 * that says what the number is.
 */
export function chipMeasure(value: number): string {
  return formatNumber(Math.abs(value) >= 100
    ? Math.round(value)
    : Math.round(value * 10) / 10)
}
